import React, { useEffect, useRef } from "react";
import {
  envelopeLayout,
  envelopeDragToPatch,
  ENV_BANDS,
  playNote,
  audioSupported,
} from "../lib/synthLab";
import { cn } from "../lib/utils";

const CAN_PLAY = audioSupported();

const HANDLES = ["attack", "sustain", "release"];
const HANDLE_TITLE = {
  attack: "Attack — drag left/right",
  sustain: "Decay & sustain — drag ↔ for decay, ↕ for sustain",
  release: "Release — drag left/right",
};

// A live ADSR envelope diagram — the slow volume contour of a note (attack →
// decay → sustain → release), distinct from the oscilloscope's fast waveform.
// Each stage gets a fixed horizontal band so a long release can't squash the
// rest; within its band a stage's handle reflects the knob's normalized value.
//
// Pass `onChange(paramId, value)` to make it an editor: the handles become
// draggable and drive the synth params (the start point stays pinned at zero).
// Without it, the graph is a static, read-only illustration.
const EnvelopeGraph = ({ patch, labeled = false, height = 96, onChange, className }) => {
  const interactive = typeof onChange === "function";
  const containerRef = useRef(null);
  const draggingRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const patchRef = useRef(patch);
  patchRef.current = patch;

  const { points, handles } = envelopeLayout(patch);
  const pts = points.map((p) => `${(p.x * 100).toFixed(2)},${(p.y * 100).toFixed(2)}`).join(" ");

  // Drag handling lives on window so the pointer can leave the small handles.
  useEffect(() => {
    if (!interactive) return undefined;
    const move = (e) => {
      const handle = draggingRef.current;
      if (!handle || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width;
      const ny = (e.clientY - rect.top) / rect.height;
      const updates = envelopeDragToPatch(handle, nx, ny);
      Object.entries(updates).forEach(([k, v]) => onChangeRef.current(k, v));
    };
    const up = () => {
      if (!draggingRef.current) return;
      draggingRef.current = null;
      if (CAN_PLAY) playNote(patchRef.current, 60, 850); // hear the result
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [interactive]);

  return (
    <div className={cn("w-full", className)}>
      <div ref={containerRef} className="relative w-full" style={{ height, touchAction: "none" }}>
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full text-primary"
          aria-hidden
        >
          <polygon points={pts} fill="currentColor" opacity="0.12" />
          <polyline
            points={pts}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {interactive &&
          HANDLES.map((h) => (
            <button
              key={h}
              type="button"
              title={HANDLE_TITLE[h]}
              aria-label={HANDLE_TITLE[h]}
              onPointerDown={(e) => {
                e.preventDefault();
                e.currentTarget.setPointerCapture?.(e.pointerId);
                draggingRef.current = h;
              }}
              className="absolute z-10 h-4 w-4 -translate-x-1/2 -translate-y-1/2 cursor-grab touch-none rounded-full border-2 border-background bg-primary shadow transition-transform hover:scale-110 active:scale-110 active:cursor-grabbing"
              style={{ left: `${handles[h].x * 100}%`, top: `${handles[h].y * 100}%` }}
            />
          ))}
      </div>
      {labeled && (
        <div className="mt-1 flex text-[0.65rem] font-medium text-muted-foreground">
          <span style={{ width: `${ENV_BANDS.attack * 100}%` }} className="text-center">A</span>
          <span style={{ width: `${ENV_BANDS.decay * 100}%` }} className="text-center">D</span>
          <span style={{ width: `${ENV_BANDS.sustain * 100}%` }} className="text-center">S</span>
          <span style={{ width: `${ENV_BANDS.release * 100}%` }} className="text-center">R</span>
        </div>
      )}
    </div>
  );
};

export default EnvelopeGraph;
